import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgencyWS } from "@/hooks/useAgencyWS";
import { warnDev } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Activity, Clock, CheckCircle2, XCircle, Users, Download, FileText,
  Hourglass, PlayCircle, Ban, UserX, Building2,
} from "lucide-react";
import { DashboardFilters } from "@/components/admin/DashboardFilters";
import { KpiCard, ChartCard, fmtDuration } from "@/components/admin/DashboardWidgets";

const FALLBACK_COLORS = ["#DC2626", "#16A34A", "#2563EB", "#7C3AED", "#EA580C", "#64748B", "#D97706"];
const EMPTY_FILTERS = {
  agency_id: "", category_code: "", counter_id: "", agent_id: "",
  date_start: "", date_end: "",
};

function buildParams(filters) {
  const p = {};
  Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
  return p;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counters, setCounters] = useState([]);
  const [agents, setAgents] = useState([]); // user list (COUNTER_AGENT)

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [overview, setOverview] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [byCounter, setByCounter] = useState([]);
  const [byAgent, setByAgent] = useState([]);
  const [byAgency, setByAgency] = useState([]);
  const isGlobal = user?.role === "SUPER_ADMIN" || user?.role === "AUDITOR";

  /* Load filter sources once */
  useEffect(() => {
    api.get("/agencies").then((r) => setAgencies(r.data || []))
      .catch((e) => warnDev("agencies", e));
    api.get("/categories").then((r) => setCategories(r.data || []))
      .catch((e) => warnDev("categories", e));
    api.get("/counters").then((r) => setCounters(r.data || []))
      .catch((e) => warnDev("counters", e));
    api.get("/users").then((r) => {
      const us = (r.data || []).filter((u) => u.role === "COUNTER_AGENT");
      setAgents(us);
    }).catch((e) => warnDev("users", e));
  }, []);

  /* Fetch all stats whenever filters change */
  const fetchStats = useCallback(async () => {
    const params = buildParams(filters);
    try {
      const calls = [
        api.get("/stats/overview", { params }),
        api.get("/stats/hourly", { params }),
        api.get("/stats/by-category", { params }),
        api.get("/stats/by-counter", { params }),
        api.get("/stats/by-agent", { params }),
      ];
      if (isGlobal) calls.push(api.get("/stats/by-agency", { params }));
      const [ov, hr, bc, bcnt, bag, bagy] = await Promise.all(calls);
      setOverview(ov.data);
      setHourly(hr.data || []);
      setByCategory(bc.data || []);
      setByCounter(bcnt.data || []);
      setByAgent(bag.data || []);
      setByAgency(bagy?.data || []);
    } catch (e) {
      warnDev("stats fetch failed", e);
    }
  }, [filters, isGlobal]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* Live refresh */
  useAgencyWS(filters.agency_id || null, useCallback(() => fetchStats(), [fetchStats]));
  useEffect(() => {
    const t = setInterval(fetchStats, 30000);
    return () => clearInterval(t);
  }, [fetchStats]);

  /* Exports */
  const downloadCsv = async () => {
    const params = new URLSearchParams(buildParams(filters));
    const res = await api.get(`/export/tickets.csv?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tickets_${overview?.today || "today"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const params = new URLSearchParams(buildParams(filters));
    const res = await api.get(`/export/daily-report.pdf?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport_journalier_${overview?.today || "today"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* Memoized chart-ready data */
  const hourlySeries = useMemo(() => {
    const map = new Map(hourly.map((h) => [h.hour, h.count]));
    return Array.from({ length: 24 }, (_, i) => ({ hour: i, count: map.get(i) || 0 }));
  }, [hourly]);

  const counterChart = useMemo(() => byCounter.map((c) => ({
    name: c.counter_name || "—",
    completed: c.count,
    avg_service: Math.round((c.avg_service_seconds || 0) / 60),
  })), [byCounter]);

  const categoryWaitChart = useMemo(() => byCategory.map((c) => ({
    code: c.code,
    label: c.label,
    color: c.color || "#64748B",
    avg_wait_min: Math.round((c.avg_wait_seconds || 0) / 60),
    count: c.count,
  })), [byCategory]);

  const agencyChart = useMemo(() => byAgency.map((a) => ({
    name: a.agency_code || a.agency_name || "—",
    total: a.total,
    completed: a.completed,
    avg_wait_min: Math.round((a.avg_wait_seconds || 0) / 60),
  })), [byAgency]);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-900">Tableau de bord</h1>
          <p className="text-slate-500 text-sm mt-1">
            Indicateurs en temps réel —
            {overview?.filters?.date_start || overview?.filters?.date_end
              ? ` ${overview?.filters?.date_start || "…"} → ${overview?.filters?.date_end || "…"}`
              : ` ${overview?.today || "—"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadCsv} variant="outline" data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={downloadPdf} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="export-pdf">
            <FileText className="w-4 h-4 mr-2" /> Rapport PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <DashboardFilters
        user={user}
        agencies={agencies}
        categories={categories}
        counters={counters}
        agents={agents}
        value={filters}
        onChange={setFilters}
        onReset={() => setFilters(EMPTY_FILTERS)}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KpiCard label="Total" value={overview?.total} icon={Activity} color="#DC2626" testid="kpi-total" />
        <KpiCard label="En attente" value={overview?.waiting} icon={Hourglass} color="#64748B" testid="kpi-waiting" />
        <KpiCard label="En cours" value={overview?.serving} icon={PlayCircle} color="#2563EB" testid="kpi-serving" />
        <KpiCard label="Terminés" value={overview?.completed} icon={CheckCircle2} color="#16A34A" testid="kpi-completed" />
        <KpiCard label="Absents" value={overview?.missed} icon={UserX} color="#EA580C" testid="kpi-missed" />
        <KpiCard label="Annulés" value={overview?.cancelled} icon={Ban} color="#DC2626" testid="kpi-cancelled" />
        <KpiCard label="Attente moy." value={fmtDuration(overview?.avg_wait_seconds)}
                 icon={Clock} color="#0EA5E9" testid="kpi-avg-wait" />
        <KpiCard label="Traitement moy." value={fmtDuration(overview?.avg_service_seconds)}
                 icon={Clock} color="#7C3AED" testid="kpi-avg-service" />
      </div>

      {/* Row 1: hourly + category distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Évolution horaire (création de tickets)" testid="chart-hourly">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={hourlySeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="hour" stroke="#64748B" tickFormatter={(h) => `${h}h`} />
              <YAxis stroke="#64748B" allowDecimals={false} />
              <Tooltip labelFormatter={(h) => `${h}h00`} />
              <Line type="monotone" dataKey="count" stroke="#DC2626" strokeWidth={2.5}
                    dot={{ fill: "#DC2626", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Répartition par catégorie" testid="chart-category-share">
          {(byCategory.length || 0) === 0 ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byCategory} dataKey="count" nameKey="code"
                     cx="50%" cy="50%" outerRadius={88} innerRadius={48}>
                  {byCategory.map((c, i) => (
                    <Cell key={c.code} fill={c.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Attente moyenne par catégorie (min)" testid="chart-category-wait">
          {categoryWaitChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryWaitChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="code" stroke="#64748B" />
                <YAxis stroke="#64748B" allowDecimals={false} />
                <Tooltip formatter={(v) => `${v} min`} labelFormatter={(c) => c} />
                <Bar dataKey="avg_wait_min" radius={[4,4,0,0]}>
                  {categoryWaitChart.map((c) => <Cell key={c.code} fill={c.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Row 2: counter perf + agency comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Performance par guichet (tickets terminés + temps moyen)" testid="chart-by-counter">
          {counterChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={counterChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" />
                <YAxis yAxisId="left" stroke="#16A34A" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" stroke="#7C3AED" allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="completed" fill="#16A34A" name="Terminés" radius={[4,4,0,0]} />
                <Bar yAxisId="right" dataKey="avg_service" fill="#7C3AED" name="Service moy. (min)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {isGlobal ? (
          <ChartCard title="Comparaison entre agences" testid="chart-by-agency">
            {agencyChart.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agencyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" />
                  <YAxis yAxisId="left" stroke="#DC2626" allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" stroke="#0EA5E9" allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="total" fill="#DC2626" name="Total" radius={[4,4,0,0]} />
                  <Bar yAxisId="left" dataKey="completed" fill="#16A34A" name="Terminés" radius={[4,4,0,0]} />
                  <Bar yAxisId="right" dataKey="avg_wait_min" fill="#0EA5E9" name="Attente moy. (min)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        ) : (
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-5 h-full flex items-center justify-center text-slate-400 text-sm">
              <Building2 className="w-5 h-5 mr-2" />
              La comparaison inter-agences est réservée aux super-admin et auditeurs.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tables: tickets per category / counter / agent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatTable
          title="Tickets par catégorie"
          headers={["Catégorie", "Volume"]}
          rows={byCategory.map((c) => [c.label, c.count])}
          testid="table-by-category"
        />
        <StatTable
          title="Tickets par guichet"
          headers={["Guichet", "Tickets", "Service moy."]}
          rows={byCounter.map((c) => [c.counter_name || "—", c.count, fmtDuration(c.avg_service_seconds)])}
          testid="table-by-counter"
        />
        <StatTable
          title="Tickets par agent"
          headers={["Agent", "Tickets", "Service moy."]}
          rows={byAgent.map((a) => [a.agent || "—", a.count, fmtDuration(a.avg_service_seconds)])}
          testid="table-by-agent"
        />
      </div>
    </div>
  );
}

function StatTable({ title, headers, rows, testid }) {
  return (
    <Card className="border-slate-200 shadow-none" data-testid={testid}>
      <CardContent className="p-5">
        <div className="font-display font-bold text-base text-slate-900 mb-3">{title}</div>
        {rows.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">Aucune donnée</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>{headers.map((h, i) => (
                <th key={h} className={`py-2 ${i === headers.length - 1 ? "text-right" : ""}`}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.slice(0, 8).map((r, ri) => (
                <tr key={ri} className="border-t border-slate-100">
                  {r.map((cell, ci) => (
                    <td key={ci} className={`py-2 ${ci === r.length - 1 ? "text-right font-mono font-bold text-slate-900" : "text-slate-700"}`}>
                      {cell ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return <div className="h-full flex items-center justify-center text-slate-400 text-sm">Aucune donnée</div>;
}
